/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import TeletLogo from 'src/assets/logo/tellet-logo-main.svg';
import * as Yup from 'yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconLoader2 } from '@tabler/icons-react';
import { StatusCodes } from 'http-status-codes';
import { getErrorMessage, getErrorStatus } from '@common/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Image from '@/components/atoms/Image';
import Routes from '@/router/routes';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import ToastMessage from '@/components/toast-message/ToastMessage';
import { API_ENDPOINTS } from '@/api-endpoints';

// Mirrors backend phoneRegex in backend/libs/common/src/types/user.types.ts:20.
const phoneRegex = /^\+[1-9]\d{6,20}$/;

// TELV1-278 F1: form shape includes optional heal fields that are only rendered
// (and only required) when the preflight tells us the user is in half-state.
const defaultValues = {
  password: '',
  confirmPassword: '',
  name: '',
  last_name: '',
  phone_number: '',
};

type TPasswordResetForm = typeof defaultValues;

const buildSchema = (needsHeal: boolean) =>
  Yup.object().shape({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
      .matches(/[0-9]/, 'Password must contain at least one number')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('This field is required'),
    name: needsHeal
      ? Yup.string().trim().required('First name is required')
      : Yup.string().notRequired(),
    last_name: needsHeal
      ? Yup.string().trim().required('Last name is required')
      : Yup.string().notRequired(),
    phone_number: Yup.string()
      .notRequired()
      .test('phone-format', 'Phone must be in international format e.g. +31201234567', (value) => {
        if (!value || value.trim() === '') return true;
        return phoneRegex.test(value.trim());
      }),
  });

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenAccept = searchParams.get('token');

  // TELV1-278 F1: preflight gates whether to render heal fields.
  // While loading: render a minimal placeholder rather than the wrong form.
  // On failure: assume profile complete (worst case: server-side B5 will
  // 400 with PROFILE_INCOMPLETE if heal fields are actually required and
  // missing, so the user retries with a clear error).
  const [preflightLoading, setPreflightLoading] = useState(true);
  const [needsHeal, setNeedsHeal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues,
    resolver: yupResolver(buildSchema(needsHeal)),
  });

  useEffect(() => {
    let cancelled = false;
    const runPreflight = async () => {
      if (!tokenAccept) {
        // No token in URL at all — let the user attempt submit; backend will
        // 401 and we'll surface the standard error. This shouldn't happen
        // in normal flow (reset link always has ?token=...).
        setPreflightLoading(false);
        return;
      }
      try {
        const res = await axiosInstanceNoAuth.get(API_ENDPOINTS.USERS.RESET_PASSWORD_PREFLIGHT, {
          headers: { Authorization: `Bearer ${tokenAccept}` },
        });
        if (cancelled) return;
        const profileComplete = !!res.data?.profile_complete;
        setNeedsHeal(!profileComplete);
      } catch {
        // Network / token error. Fall through with needsHeal=false. If the
        // user IS half-state, backend B5 will return 400 PROFILE_INCOMPLETE
        // on submit and we'll surface a clear retry message.
      } finally {
        if (!cancelled) setPreflightLoading(false);
      }
    };
    runPreflight();
    return () => {
      cancelled = true;
    };
  }, [tokenAccept]);

  const onSubmit: SubmitHandler<TPasswordResetForm> = async (formData) => {
    try {
      // Strip the client-only confirmPassword field. Always send password.
      // Send heal fields ONLY when in heal mode (avoids accidentally writing
      // empty strings over a profile that already has values — though the
      // backend's race-safe conditional filter would catch it too).
      const payload: Record<string, string> = { password: formData.password };
      if (needsHeal) {
        payload.name = formData.name.trim();
        payload.last_name = formData.last_name.trim();
        if (formData.phone_number && formData.phone_number.trim()) {
          payload.phone_number = formData.phone_number.trim();
        }
      }

      await axiosInstanceNoAuth.post(API_ENDPOINTS.USERS.RESET_PASSWORD, payload, {
        headers: { Authorization: `Bearer ${tokenAccept}` },
      });

      // TELV1-371 — removed `setIsLoading(true)` here. It was dead state:
      // the `navigate(Routes.login.path)` below unmounts the component
      // within microseconds, so the loading spinner this set would never
      // be seen. The button's "Reset password" label is gated on
      // `isSubmitting` from react-hook-form (line 246) during the await,
      // not on `isLoading`. The `finally { setIsLoading(false) }` still
      // runs and matters for the TELV1-370 error branches below (which
      // early-return without navigating, leaving the form mounted).
      toast.success(
        <ToastMessage
          title="Password changed successfully"
          description={
            needsHeal
              ? 'Your account is now set up. Try to login.'
              : 'Password changed successfully try to login now.'
          }
          isAuthScreen
        />,
      );

      navigate(Routes.login.path);
    } catch (err) {
      // TELV1-370 — branch on status so users get an actionable message
      // instead of a generic "Error occurred". The network-failure branch
      // is load-bearing: it tells the user their password MAY have been
      // saved (the request reached the server but the response didn't
      // reach the browser) so they should try logging in BEFORE retrying.
      // Without it, users assume reset failed and escalate to support
      // instead of trying to log in. See TELV1-334 (Nongnoot, Thailand)
      // for the reference incident.
      const status = getErrorStatus(err);
      const serverMessage = getErrorMessage(err);

      if (!status || status === 'unknown') {
        // Network drop / CORS preflight blocked / DNS failure. The server
        // may have completed the write — instruct the user to try login
        // before requesting another reset.
        toast.error(
          'Network error — your password may have been saved. Try logging in. If that fails, request a new reset link.',
        );
        return;
      }
      if (status === StatusCodes.UNAUTHORIZED) {
        // EmailAuthGuard rejected the JWT — token expired, malformed, or
        // signature invalid. The reset link is no longer usable; send the
        // user back to forgot-password to request a fresh one.
        toast.error('Your reset link has expired. Please request a new one.');
        navigate(Routes.forgot_password.path);
        return;
      }
      if (status === StatusCodes.BAD_REQUEST) {
        // Currently this endpoint only returns 400 for PROFILE_INCOMPLETE
        // (needsHeal && (!dto.name || !dto.last_name)) — see users.service.ts:929.
        // Surface the actual server message if useful, otherwise fall back
        // to the heal-field copy.
        toast.error(serverMessage || 'Please fill in your first name and last name.');
        return;
      }
      if (status === StatusCodes.NOT_FOUND) {
        // Extreme edge: account deleted between forgot-password and submit.
        // The reset token is bound to a userId that no longer resolves;
        // user must restart the flow.
        toast.error('Account not found. Please request a new reset link.');
        navigate(Routes.forgot_password.path);
        return;
      }
      // 500 / 502 / 503 / 504 / any unhandled 4xx fall here. Show the
      // server message when available so debugging support tickets is
      // straightforward.
      toast.error(
        serverMessage || 'Something went wrong on our side. Please try again, or contact support.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (preflightLoading) {
    // Avoid flashing the wrong form while we discover whether to show heal
    // fields. Brief render (a fetch of ~50-200ms).
    return (
      <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
        <div className="flex items-center gap-2 text-[#2a3547]">
          <IconLoader2 className="w-5 h-5 animate-spin" />
          <span>Loading…</span>
        </div>
      </main>
    );
  }

  return (
    <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
      <div className="w-full max-w-[600px] min-h-screen center border-0 px-2 py-4 min450:bg-white min450:max-w-full">
        <div className="flex flex-col items-center w-full max-w-[600px] rounded-md h-fit p-7 min450:p-5">
          <div className="flex flex-col items-center w-full ring-1 ring-neutral-200 max-w-[500px] min450:ring-transparent min450:shadow-none gap-10 bg-white rounded-md shadow-md h-fit p-7">
            <Image effect="blur" src={TeletLogo} alt="logo" width={115} height={37} />
            {needsHeal && (
              <p className="text-sm text-[#2a3547] text-center">
                Welcome to Tellet — we noticed your account setup is not complete. Please fill in
                your details below to finish setting up your account.
              </p>
            )}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col items-start w-full gap-5 h-fit"
            >
              {needsHeal && (
                <>
                  <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                    First name
                    <Input {...register('name')} type="text" hasError={!!errors.name} />
                    {errors.name?.message && (
                      <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                        {errors.name?.message}
                      </p>
                    )}
                  </label>
                  <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                    Last name
                    <Input {...register('last_name')} type="text" hasError={!!errors.last_name} />
                    {errors.last_name?.message && (
                      <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                        {errors.last_name?.message}
                      </p>
                    )}
                  </label>
                  <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                    Phone number (optional, international format e.g. +31201234567)
                    <Input
                      {...register('phone_number')}
                      type="tel"
                      hasError={!!errors.phone_number}
                    />
                    {errors.phone_number?.message && (
                      <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                        {errors.phone_number?.message}
                      </p>
                    )}
                  </label>
                </>
              )}
              <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                Password
                <Input {...register('password')} type="password" hasError={!!errors.password} />
                {errors.password?.message && (
                  <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                    {errors.password?.message}
                  </p>
                )}
              </label>
              <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                Confirm password
                <Input
                  {...register('confirmPassword')}
                  type="password"
                  hasError={!!errors.confirmPassword}
                />
                {errors.confirmPassword?.message && (
                  <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                    {errors.confirmPassword?.message}
                  </p>
                )}
              </label>
              <div className="relative flex flex-col w-full gap-7">
                <Button
                  disabled={isSubmitting || isLoading}
                  variant="default"
                  type="submit"
                  className="flex items-center gap-0.5 mt-4"
                >
                  {isSubmitting && <IconLoader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <span>{needsHeal ? 'Complete account setup' : 'Reset password'}</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ResetPassword;
